import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Spinner, Table } from "react-bootstrap";
import { Link } from "react-router";
import { ChevronDown, ChevronRight } from "react-feather";
import { apiErrorMessage, useGetLazy } from "../../api";
import {
  IKindredChangeLogEntry,
  IKindredChangeLogFilter,
  IKindredChangeLogResponse,
  KindredChangeEntityType,
  KindredChangeSource,
} from "../../interfaces";
import { EmptyState } from "../ui";

export const CHANGE_SOURCE_LABEL: Record<KindredChangeSource, string> = {
  [KindredChangeSource.Edit]: "Edit",
  [KindredChangeSource.Create]: "Create",
  [KindredChangeSource.Targeting]: "Targeting",
  [KindredChangeSource.BulkUpdate]: "Bulk update",
  [KindredChangeSource.DomainReview]: "Domain review",
  [KindredChangeSource.BrandDomainApproval]: "Brand domain approval",
  [KindredChangeSource.MerchantMapEdit]: "Merchant map edit",
};

export const CHANGE_ENTITY_LABEL: Record<KindredChangeEntityType, string> = {
  deal: "Deal",
  coupon: "Coupon",
  offer: "Offer",
  brand: "Brand",
  brand_link: "Brand link",
  brand_domain: "Brand registry",
  coupon_target: "Coupon target",
  merchant_map: "Merchant map",
};

const entityPath = (e: IKindredChangeLogEntry): string | null => {
  switch (e.entityType) {
    case "deal":
      return `/kindred/deals/${e.entityId}`;
    case "coupon":
      return `/kindred/coupons/${e.entityId}`;
    case "offer":
      return `/kindred/offers/${e.entityId}`;
    case "brand":
      return `/kindred/brands/${e.entityId}`;
    default:
      return null;
  }
};

const show = (v: unknown) =>
  v === null || v === undefined ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v);

/** Field-by-field before/after, for one entry. */
const Diff: React.FC<{ entry: IKindredChangeLogEntry }> = ({ entry }) => {
  const keys = Array.from(
    new Set([...Object.keys(entry.before ?? {}), ...Object.keys(entry.after ?? {})])
  );
  if (keys.length === 0) {
    return <div className="text-muted small">No field values recorded.</div>;
  }
  return (
    <Table size="sm" className="small mb-0" style={{ tableLayout: "fixed" }}>
      <thead>
        <tr>
          <th style={{ width: "20%" }}>Field</th>
          <th style={{ width: "40%" }}>Before</th>
          <th style={{ width: "40%" }}>After</th>
        </tr>
      </thead>
      <tbody>
        {keys.map((k) => (
          <tr key={k}>
            <td className="fw-semibold">{k}</td>
            <td className="text-danger-emphasis text-break">{show(entry.before?.[k])}</td>
            <td className="text-success-emphasis text-break">{show(entry.after?.[k])}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

/**
 * Our own record of every change the app makes to Kindred data. Pass any
 * filter (entity, brand, domain, run…) to scope it.
 */
export const ChangeLogTable: React.FC<{
  filter: IKindredChangeLogFilter;
  pageSize?: number;
  /** Bump to reload, e.g. after a change was applied. */
  refreshKey?: number;
  onActorOptions?: (actors: string[]) => void;
  emptyTitle?: string;
}> = ({ filter, pageSize = 25, refreshKey, onActorOptions, emptyTitle }) => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<IKindredChangeLogResponse | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [failed, setFailed] = useState<string | null>(null);
  const { execute, loading } = useGetLazy<IKindredChangeLogResponse>();
  const filterKey = JSON.stringify(filter);

  const load = useCallback(
    async (p: number) => {
      const params = new URLSearchParams({ page: String(p), limit: String(pageSize) });
      for (const [k, v] of Object.entries(filter)) {
        if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
      }
      const fetchOnce = () => execute(`kindred-change-log?${params}`);
      // One quiet retry: the API restarting (e.g. a dev reload) drops the first request.
      const r = await fetchOnce()
        .catch(() => new Promise((res) => setTimeout(res, 2000)).then(fetchOnce))
        .catch((e) => {
          setFailed(apiErrorMessage(e));
          return null;
        });
      if (r) {
        setFailed(null);
        setData(r);
        onActorOptions?.(r.actorOptions);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [execute, filterKey, pageSize]
  );

  useEffect(() => {
    setPage(1);
    load(1);
  }, [load, refreshKey]);

  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (failed && !data) {
    return (
      <Alert variant="warning" className="small mb-0 d-flex align-items-center">
        <span>
          Couldn't load the change log ({failed}). The API didn't respond; it may have been restarting.
        </span>
        <Button size="sm" variant="outline-secondary" className="ms-auto" onClick={() => load(page)} disabled={loading}>
          {loading ? <Spinner animation="border" size="sm" /> : "Retry"}
        </Button>
      </Alert>
    );
  }
  if (!data) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }
  if (data.entries.length === 0) {
    return (
      <EmptyState
        icon="bi-journal-text"
        title={emptyTitle ?? "No changes recorded"}
        message="Changes made from Domain Review, the deal, coupon, offer and brand pages, coupon targeting, bulk updates and brand-domain approvals are recorded here."
      />
    );
  }

  const totalPages = Math.max(1, Math.ceil(data.total / data.limit));
  return (
    <div>
      <Table size="sm" hover responsive className="small align-middle mb-2">
        <thead>
          <tr>
            <th style={{ width: 24 }} />
            <th>When</th>
            <th>Who</th>
            <th>What</th>
            <th>Row</th>
            <th>Brand</th>
            <th>Reason / summary</th>
          </tr>
        </thead>
        <tbody>
          {data.entries.map((e) => {
            const path = entityPath(e);
            const isOpen = open.has(e._id);
            return (
              <React.Fragment key={e._id}>
                <tr role="button" onClick={() => toggle(e._id)}>
                  <td>{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                  <td className="text-nowrap">{new Date(e.createdAt).toLocaleString()}</td>
                  <td>{e.actorName ?? e.actor}</td>
                  <td>
                    <Badge bg="secondary" className="me-1">
                      {CHANGE_SOURCE_LABEL[e.source] ?? e.source}
                    </Badge>
                    {e.action.replace(/_/g, " ")}
                  </td>
                  <td className="text-truncate" style={{ maxWidth: 220 }} title={e.entityId}>
                    <span className="text-muted me-1">
                      {CHANGE_ENTITY_LABEL[e.entityType] ?? e.entityType}
                    </span>
                    {path ? (
                      <Link target="_blank" rel="noopener noreferrer" to={path} onClick={(ev) => ev.stopPropagation()}>
                        {e.entityLabel ?? e.entityId}
                      </Link>
                    ) : (
                      e.entityLabel ?? e.entityId
                    )}
                  </td>
                  <td>
                    {e.brandId ? (
                      <Link target="_blank" rel="noopener noreferrer" to={`/kindred/brands/${e.brandId}`} onClick={(ev) => ev.stopPropagation()}>
                        {e.brandName ?? e.brandId.slice(0, 8)}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="text-truncate" style={{ maxWidth: 320 }} title={e.comment ?? e.summary ?? ""}>
                    {e.comment ?? e.summary ?? "—"}
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td />
                    <td colSpan={6} className="bg-body-tertiary">
                      {e.summary && e.comment && <div className="small mb-1">{e.summary}</div>}
                      {e.domains.length > 0 && (
                        <div className="small text-muted mb-1">Domains: {e.domains.join(", ")}</div>
                      )}
                      <Diff entry={e} />
                      {e.runId && (
                        <div className="small text-muted mt-1">Run {e.runId}</div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
      <div className="d-flex align-items-center gap-2 small">
        <span className="text-muted">
          {data.total} change{data.total === 1 ? "" : "s"}
        </span>
        {loading && <Spinner animation="border" size="sm" />}
        {totalPages > 1 && (
          <span className="ms-auto d-flex align-items-center gap-2">
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page <= 1}
              onClick={() => {
                setPage(page - 1);
                load(page - 1);
              }}
            >
              Previous
            </Button>
            Page {page} of {totalPages}
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page >= totalPages}
              onClick={() => {
                setPage(page + 1);
                load(page + 1);
              }}
            >
              Next
            </Button>
          </span>
        )}
      </div>
    </div>
  );
};
